import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { motion } from "framer-motion";
import {
  Plus, Play, Loader2, Building2, User, Mail, Calendar,
  Trash2, ArrowRightLeft, ChevronDown, ChevronUp, Briefcase
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { FW_NAMES, FW_COLORS } from "../constants/frameworks";


/**
 * Reusable engagements panel for Risk and SoA pages.
 * Allows consultants to manage client assessment projects.
 *
 * Props:
 *   - assessmentType: "risk" | "soa"
 *   - runEndpoint: "run-risk" | "run-soa"
 *   - titleKey: i18n key for panel title (e.g. "risks.assessments" / "soa.assessments")
 *   - newButtonKey: i18n key for new button label
 */
const EngagementsPanel = ({ assessmentType, runEndpoint, titleKey, newButtonKey }) => {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const confirm = useConfirm();
  const [expanded, setExpanded] = useState(true);
  const [projects, setProjects] = useState([]);
  const [managedOrgs, setManagedOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState("");
  const [form, setForm] = useState({
    client_org_id: "", project_name: "", contact_name: "", contact_email: "",
    framework: "iso27001", responsible: "", notes: ""
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API}/engagements?assessment_type=${assessmentType}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants/managed-orgs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(pRes.data || []);
      setManagedOrgs(oRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, assessmentType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setForm({
      client_org_id: managedOrgs[0]?.organization_id || "", project_name: "",
      contact_name: "", contact_email: "", framework: "iso27001", responsible: "", notes: ""
    });
    setShowCreate(true);
  };

  const submitCreate = async () => {
    if (!form.client_org_id || !form.project_name) {
      toast.error(t("eng.client_project_required"));
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form, assessment_type: assessmentType };
      const res = await axios.post(`${API}/engagements`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("eng.project_created_running"));
      setShowCreate(false);
      await runProject(res.data.engagement_id);
      fetchData();
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setCreating(false); }
  };

  const runProject = async (engagementId) => {
    setRunning(engagementId);
    try {
      await axios.post(`${API}/engagements/${engagementId}/${runEndpoint}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("eng.snapshot_updated"));
      fetchData();
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setRunning(""); }
  };

  const deleteProject = async (engagementId) => {
    if (!(await confirm({ title: t("eng.delete_title"), description: t("eng.delete_confirm"), destructive: true }))) return;
    try {
      await axios.delete(`${API}/engagements/${engagementId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("eng.deleted"));
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const switchAndOpen = async (clientOrgId, clientName) => {
    if (!(await confirm({
      title: t("eng.switch_title"),
      description: `${t("eng.switch_confirm_prefix")} "${clientName}" ${t("eng.switch_confirm_suffix")}`,
    }))) return;
    try {
      await axios.put(`${API}/tenants/switch/${clientOrgId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("eng.context_switched"));
      setTimeout(() => window.location.reload(), 800);
    } catch { toast.error("Failed"); }
  };

  const currentOrgId = user?.organization_id;

  const renderSnapshot = (snap) => {
    if (!snap) return <p className="text-xs text-muted-foreground italic">{t("eng.no_snapshot")}</p>;
    if (snap.assessment_type === "risk") {
      return (
        <div className="space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-muted-foreground">{t("eng.risks_label")}:</span><span className="font-bold">{snap.total_risks}</span></div>
          <div className="flex justify-between"><span className="text-red-500">{t("critical")}:</span><span>{snap.by_level?.critical || 0}</span></div>
          <div className="flex justify-between"><span className="text-orange-500">{t("high")}:</span><span>{snap.by_level?.high || 0}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("eng.treated_label")}:</span><span>{snap.treatment_ratio}%</span></div>
        </div>
      );
    }
    if (snap.assessment_type === "soa") {
      return (
        <div className="space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-muted-foreground">{t("gap.coverage")}:</span><span className="font-bold">{snap.coverage_percentage}%</span></div>
          <div className="flex justify-between"><span className="text-green-500">{t("implemented")}:</span><span>{snap.implemented}</span></div>
          <div className="flex justify-between"><span className="text-yellow-500">{t("in_progress")}:</span><span>{snap.in_progress}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("eng.entries_label")}:</span><span>{snap.total_entries}</span></div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid={`engagements-panel-${assessmentType}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 flex-1 text-left">
            <Briefcase className="w-5 h-5 text-primary" />
            <div>
              <h3 className="font-heading font-bold">{t(titleKey)}</h3>
              <p className="text-xs text-muted-foreground">
                {projects.length} {t("eng.projects_label")}
              </p>
            </div>
            {expanded ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
          </button>
          <Button size="sm" onClick={(e) => { e.stopPropagation(); openCreate(); }} data-testid={`new-${assessmentType}-engagement-btn`}>
            <Plus className="w-4 h-4 mr-1" />{t(newButtonKey)}
          </Button>
        </div>

        {expanded && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : projects.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-4 text-center">
                {t("ux.no_projects_yet")}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map(p => {
                  const isCurrent = p.client_org_id === currentOrgId;
                  return (
                    <motion.div key={p.engagement_id} whileHover={{ scale: 1.01 }}>
                      <Card className={`border-border ${isCurrent ? "ring-1 ring-primary" : ""}`} data-testid={`${assessmentType}-eng-${p.engagement_id}`}>
                        <CardContent className="pt-4 space-y-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-sm truncate">{p.project_name}</h4>
                              <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                <Building2 className="w-3 h-3" />{p.client_name}
                              </p>
                            </div>
                            {isCurrent && <Badge variant="default" className="text-[10px]">{t("eng.current_badge")}</Badge>}
                          </div>

                          <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: FW_COLORS[p.framework] }} />
                            <span className="text-[11px]">{FW_NAMES[p.framework]}</span>
                          </div>

                          {renderSnapshot(p.result_snapshot)}

                          <div className="space-y-0.5 text-[10px] text-muted-foreground border-t border-border pt-1.5">
                            {p.contact_name && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.contact_name}</div>}
                            {p.contact_email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{p.contact_email}</div>}
                            {p.responsible && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.responsible}</div>}
                            {p.result_snapshot?.ran_at && <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.result_snapshot.ran_at).toLocaleDateString()}</div>}
                          </div>

                          <div className="flex gap-1.5">
                            {!isCurrent && (
                              <Button size="sm" variant="outline" className="flex-1 h-7 text-[11px]"
                                onClick={() => switchAndOpen(p.client_org_id, p.client_name)}
                                data-testid={`open-${p.engagement_id}`}>
                                <ArrowRightLeft className="w-3 h-3 mr-1" />{t("eng.open")}
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                              onClick={() => runProject(p.engagement_id)} disabled={running === p.engagement_id}
                              data-testid={`run-${p.engagement_id}`}>
                              {running === p.engagement_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7"
                              onClick={() => deleteProject(p.engagement_id)} data-testid={`del-${p.engagement_id}`}>
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Create dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t(newButtonKey)}</DialogTitle>
              <DialogDescription>
                {t("eng.create_project_desc")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>{t("gap.select_client")} *</Label>
                {managedOrgs.length === 0 ? (
                  <p className="text-sm text-destructive">{t("gap.client_required")}</p>
                ) : (
                  <Select value={form.client_org_id} onValueChange={(v) => setForm(f => ({ ...f, client_org_id: v }))}>
                    <SelectTrigger data-testid={`${assessmentType}-client-select`}><SelectValue placeholder={t("gap.select_client")} /></SelectTrigger>
                    <SelectContent>
                      {managedOrgs.map(o => (
                        <SelectItem key={o.organization_id} value={o.organization_id}>
                          {o.name} {o.is_parent && `(${t("eng.your_org")})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("gap.project_name")} *</Label>
                  <Input value={form.project_name} onChange={(e) => setForm(f => ({ ...f, project_name: e.target.value }))}
                    placeholder={assessmentType === "risk" ? "Risk Assessment Q2" : "SoA ISO 27001"}
                    data-testid={`${assessmentType}-project-name`} />
                </div>
                <div className="space-y-2">
                  <Label>{t("gap.framework_select")} *</Label>
                  <Select value={form.framework} onValueChange={(v) => setForm(f => ({ ...f, framework: v }))}>
                    <SelectTrigger data-testid={`${assessmentType}-framework-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(FW_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("gap.contact_name")}</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>{t("gap.contact_email")}</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("gap.responsible")}</Label>
                <Input value={form.responsible} onChange={(e) => setForm(f => ({ ...f, responsible: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t("gap.notes")}</Label>
                <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>{t("cancel")}</Button>
              <Button onClick={submitCreate} disabled={creating || managedOrgs.length === 0} data-testid={`submit-${assessmentType}-eng`}>
                {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("generating")}</> : t("create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default EngagementsPanel;
